/**
 * 번개 화면용 — 자주 함께한 사람 추천 섹션.
 * - meetingId 컨텍스트 없는 사용자 기준 추천 (번개+모임 합산)
 *
 * 3가지 동작 모드:
 *  1) flashId 전달 (FlashShare 등 생성 후) — [초대] 버튼 → onPostInvite push → [초대됨]
 *  2) onSelectionChange 전달 (FlashCreate, 생성 전) — [선택] 토글, 부모가 userNos 보관 → 생성 후 일괄 초대
 *  3) shareUrl 만 전달 — 레거시 [공유] 버튼 (Web Share/클립보드 폴백)
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { FlashRepository } from 'repository/flashRepository';
import { openShare, copyToClipboard } from 'common/utils/share';
import { useAlert } from 'contexts/AlertContext';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import { IcCheck } from 'components/common/icons/EmojiIcons';

interface CoMember {
    userNo: number;
    nickname: string;
    sharedCount: number;
}

interface Props {
    /** 번개 ID — 있으면 [초대] 버튼이 직접 onPostInvite push 발사 */
    flashId?: string;
    /** 번개 share URL — 레거시 [공유] 버튼용 (Web Share / 클립보드) */
    shareUrl?: string;
    /** 번개 제목 — share text 에 포함 */
    flashTitle?: string;
    /** FlashCreate 등 생성 전 단계 — 선택된 userNos 목록 (controlled) */
    selectedUserNos?: number[];
    /** 선택 변경 시 호출 — 부모가 selectedUserNos 갱신 */
    onSelectionChange?: (userNos: number[]) => void;
    /** 한 번에 보여줄 최대 인원 */
    limit?: number;
}

const decodeNick = (raw: any): string => {
    const s = raw || '';
    try { return decodeURIComponent(s); } catch { return s; }
};

export const FlashFrequentCoMembers: React.FC<Props> = ({
    flashId,
    shareUrl,
    flashTitle,
    selectedUserNos,
    onSelectionChange,
    limit = 10,
}) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [members, setMembers] = useState<CoMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [sharingUserNo, setSharingUserNo] = useState<number | null>(null);
    const [invitingUserNo, setInvitingUserNo] = useState<number | null>(null);
    const [invitedUserNos, setInvitedUserNos] = useState<Set<number>>(new Set());
    const [expanded, setExpanded] = useState(false);
    const PREVIEW = 3;

    // 동작 모드 결정 — flashId(즉시 초대) > 선택 모드(생성 전) > 공유 모드(레거시)
    const isInviteMode = !!flashId;
    const isSelectMode = !flashId && !!onSelectionChange;
    const isShareMode = !flashId && !onSelectionChange && !!shareUrl;
    const selectedSet = new Set(selectedUserNos || []);

    const toggleSelect = (userNo: number) => {
        if (!onSelectionChange) return;
        const next = new Set(selectedUserNos || []);
        if (next.has(userNo)) next.delete(userNo);
        else next.add(userNo);
        onSelectionChange(Array.from(next));
    };

    const handleInvite = async (m: CoMember) => {
        if (!flashId || invitingUserNo) return;
        setInvitingUserNo(m.userNo);
        try {
            const res: any = await FlashRepository.onPostInvite(flashId, { inviteeUserNos: [m.userNo] });
            const httpStatus = res?.status;
            const ok = httpStatus == null || (httpStatus >= 200 && httpStatus < 300);
            if (ok) {
                setInvitedUserNos(prev => new Set(prev).add(m.userNo));
                markOnboardingCompleted('step_invite');
            } else {
                await showAlert({ message: res?.data?.message || '초대에 실패했어요' });
            }
        } catch {
            await showAlert({ message: '초대에 실패했어요' });
        } finally {
            setInvitingUserNo(null);
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        MeetingRepository.onGetFrequentCoMembersByUser(limit)
            .then((res: any) => {
                if (cancelled) return;
                const list = res?.data?.data || res?.data || [];
                setMembers(Array.isArray(list)
                    ? list.map((r: any) => ({
                        userNo: Number(r.userNo),
                        nickname: decodeNick(r.nickname || ''),
                        sharedCount: Number(r.sharedCount || 0),
                    }))
                    : []);
            })
            .catch(() => { if (!cancelled) setMembers([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [limit]);

    const handleShareTo = async (m: CoMember) => {
        if (!shareUrl || sharingUserNo) return;
        setSharingUserNo(m.userNo);
        try {
            const ok = await openShare({
                title: flashTitle || '번개 모임',
                text: `${m.nickname}님, 같이 가실래요? "${flashTitle || '번개 모임'}"`,
                url: shareUrl,
            });
            if (!ok) {
                // Web Share API 미지원 — 클립보드 폴백
                const copied = await copyToClipboard(shareUrl);
                if (!copied) await showAlert({ message: t('flash.share.copyFailed') });
            }
        } finally {
            setSharingUserNo(null);
        }
    };

    if (loading) return null;
    // 빈 상태에서도 invite/share 모드는 안내 노출 — 선택 모드는 숨김
    if (members.length === 0 && !isInviteMode && !isShareMode) return null;

    const visible = expanded ? members : members.slice(0, PREVIEW);

    if (members.length === 0) {
        return (
            <div className="flash-create-section" style={{ marginTop: 16 }}>
                <div className="flash-create-label">함께하면 좋을 사람</div>
                <div style={{
                    padding: 14, borderRadius: 10, background: '#f8fafc',
                    border: '1px dashed #cbd5e1', color: '#666', fontSize: 12, lineHeight: 1.6,
                }}>
                    아직 자주 함께한 분이 없어요.<br />
                    위의 <b>공유</b>나 <b>링크 복사</b>로 친구를 초대해보세요.
                </div>
            </div>
        );
    }

    const renderRowButton = (m: CoMember) => {
        // 1) 즉시 초대 모드 (flashId 보유)
        if (isInviteMode) {
            const isInvited = invitedUserNos.has(m.userNo);
            const isPending = invitingUserNo === m.userNo;
            return (
                <button
                    type="button"
                    disabled={isInvited || isPending}
                    onClick={() => handleInvite(m)}
                    style={{
                        minWidth: 76, padding: '6px 12px', fontSize: 12, fontWeight: 700,
                        background: isInvited ? '#e5e7eb' : '#1a73e8',
                        color: isInvited ? '#6b7280' : '#fff',
                        border: 'none', borderRadius: 8,
                        cursor: (isInvited || isPending) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {isInvited ? '초대됨' : isPending ? '초대 중…' : '초대'}
                </button>
            );
        }
        // 2) 선택 모드 (FlashCreate, 생성 전)
        if (isSelectMode) {
            const isSelected = selectedSet.has(m.userNo);
            return (
                <button
                    type="button"
                    onClick={() => toggleSelect(m.userNo)}
                    style={{
                        minWidth: 76, padding: '6px 12px', fontSize: 12, fontWeight: 700,
                        background: isSelected ? '#1a73e8' : '#fff',
                        color: isSelected ? '#fff' : '#1a73e8',
                        border: `1.5px solid ${isSelected ? '#1a73e8' : '#bfdbfe'}`,
                        borderRadius: 8, cursor: 'pointer',
                    }}
                >
                    {isSelected ? <><IcCheck size={13} /> 선택됨</> : '+ 초대'}
                </button>
            );
        }
        // 3) 공유 모드 (레거시 — shareUrl 만)
        if (isShareMode) {
            return (
                <button
                    type="button"
                    disabled={sharingUserNo === m.userNo}
                    onClick={() => handleShareTo(m)}
                    style={{
                        minWidth: 76, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                        background: '#1a73e8', color: '#fff',
                        border: 'none', borderRadius: 8,
                        cursor: sharingUserNo === m.userNo ? 'not-allowed' : 'pointer',
                    }}
                >
                    {sharingUserNo === m.userNo ? '공유 중…' : '공유'}
                </button>
            );
        }
        return null;
    };

    return (
        <div className="flash-create-section" style={{ marginTop: 16 }}>
            <div className="flash-create-label">함께하면 좋을 사람</div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
                {isSelectMode
                    ? '선택한 분들에게 번개 생성 직후 초대 푸시를 보내드려요'
                    : '과거 번개 / 모임에서 자주 함께한 분들이에요'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visible.map(m => (
                    <div key={m.userNo} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                        background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb',
                    }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: '50%', background: '#dbeafe',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 14, fontWeight: 700, color: '#1a73e8', flexShrink: 0,
                        }}>
                            {(m.nickname || '?').slice(0, 1)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {m.nickname}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                {m.sharedCount}번 함께했어요
                            </div>
                        </div>
                        {renderRowButton(m)}
                    </div>
                ))}
            </div>

            {members.length > PREVIEW && (
                <button
                    type="button"
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        width: '100%', marginTop: 10, padding: '10px 12px',
                        background: '#f8fafc', color: '#444',
                        border: '1px solid #e5e7eb', borderRadius: 8,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                >
                    {expanded ? '접기' : `더보기 (외 ${members.length - PREVIEW}명)`}
                </button>
            )}
        </div>
    );
};
